/**
 * First-time user walkthrough overlay.
 *
 * Shows a step-by-step guided tour of the Terra17 interface.
 * Steps are displayed as a modal dialog with a progress indicator.
 * The walkthrough state is persisted in localStorage so it only shows once.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import {
  NavigateNext as NextIcon,
  Check as DoneIcon,
} from "@mui/icons-material";

const WALKTHROUGH_KEY = "ifrs17_walkthrough_completed";

interface WalkthroughStep {
  title: string;
  description: string;
  highlight?: string;
}

const STEPS: WalkthroughStep[] = [
  {
    title: "Welcome to Terra17",
    description:
      "This platform replaces the 54-sheet Excel workbook for IFRS 17 PAA valuations. " +
      "It handles data entry, calculations, disclosures, and report generation — all in one place.",
  },
  {
    title: "Step 1: Enter Your Data",
    description:
      "Start by entering your company's insurance data: Gross Written Premiums, Claims Paid, " +
      "Outstanding Claims (OCR), IBNR reserves, UPR, DAC, and Risk Adjustment inputs. " +
      "Use the Data Entry section in the sidebar. You can also paste data directly from Excel.",
    highlight: "Data Entry",
  },
  {
    title: "Step 2: Configure Yield Curves",
    description:
      "Enter the NSE Yield Curve rates for each financial year. These rates are used to discount " +
      "future cash flows for LIC and compute Insurance Finance Income/Expenses (IFIE).",
    highlight: "Yield Curves",
  },
  {
    title: "Step 3: Check Data Completeness",
    description:
      "Visit the Data Completeness dashboard to see a traffic-light matrix showing which data " +
      "has been entered for each business class and year. Green means complete, red means missing.",
    highlight: "Data Completeness",
  },
  {
    title: "Step 4: Run Calculations",
    description:
      "Once all data is entered, navigate to the Calculations page and click 'Run Calculations'. " +
      "The engine will compute LRC, LIC, Loss Component, Risk Adjustment, and all disclosure tables " +
      "across all business classes and years.",
    highlight: "Calculations",
  },
  {
    title: "Step 5: Review Disclosures",
    description:
      "View the generated IFRS 17 disclosures: Disclosure 100 (Reconciliation), " +
      "Disclosure 105 (Cash Flows), Disclosure 110 (IFIE), P&L Statement, and Balance Sheet. " +
      "All values match the original workbook to within KES 0.01 tolerance.",
    highlight: "Disclosures",
  },
  {
    title: "Step 6: Export Reports",
    description:
      "Download audit-ready PDF reports or Excel templates from the Import/Export page. " +
      "PDF reports are formatted for IRA submission with all values in KES thousands.",
    highlight: "Import / Export",
  },
  {
    title: "You're All Set!",
    description:
      "You can revisit this walkthrough anytime from the Help section in the sidebar. " +
      "For questions or support, contact your Kenbright administrator.",
  },
];

export default function WalkthroughOverlay() {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const completed = localStorage.getItem(WALKTHROUGH_KEY);
    if (!completed) {
      // Show walkthrough after a short delay to let the page load
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = useCallback(() => {
    localStorage.setItem(WALKTHROUGH_KEY, "true");
    setOpen(false);
  }, []);

  const handleNext = useCallback(() => {
    if (activeStep < STEPS.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      handleClose();
    }
  }, [activeStep, handleClose]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(0, prev - 1));
  }, []);

  const handleSkip = useCallback(() => {
    localStorage.setItem(WALKTHROUGH_KEY, "true");
    setOpen(false);
  }, []);

  const progress = ((activeStep + 1) / STEPS.length) * 100;
  const currentStep = STEPS[activeStep];
  const isLastStep = activeStep === STEPS.length - 1;

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700} color="primary">
          {currentStep.title}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 1, borderRadius: 1, height: 6 }}
        />
        <Typography variant="caption" color="text.secondary">
          Step {activeStep + 1} of {STEPS.length}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7 }}>
          {currentStep.description}
        </Typography>

        {currentStep.highlight && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "primary.50",
              border: "1px solid",
              borderColor: "primary.200",
              borderRadius: 1,
              mt: 1,
            }}
          >
            <Typography variant="body2" color="primary.main" fontWeight={600}>
              Look for "{currentStep.highlight}" in the sidebar navigation.
            </Typography>
          </Box>
        )}

        {/* Compact stepper for desktop */}
        <Stepper
          activeStep={activeStep}
          alternativeLabel
          sx={{ mt: 3, display: { xs: "none", sm: "flex" } }}
        >
          {STEPS.map((_step, index) => (
            <Step key={index}>
              <StepLabel>{""}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSkip} color="inherit" size="small">
          Skip Tour
        </Button>
        <Box sx={{ flex: 1 }} />
        {activeStep > 0 && (
          <Button onClick={handleBack} variant="outlined" size="small">
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          variant="contained"
          endIcon={isLastStep ? <DoneIcon /> : <NextIcon />}
          size="small"
        >
          {isLastStep ? "Get Started" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
